import { lazy, Suspense } from 'react'
import { Routes, Route, Navigate } from 'react-router-dom'
import { clientConfig } from '@/config'
import { motion } from 'framer-motion'
import { Loader2 } from 'lucide-react'
import { ErrorBoundary } from '@/components/ErrorBoundary/ErrorBoundary'
import { Dashboard } from '@/components/Dashboard/Dashboard'
import { DashboardPage } from '@/components/Dashboard/Shell/DashboardPage'
import { ThemeProvider } from './contexts/ThemeContext'
import { ToastProvider } from './contexts/ToastContext'
import { AuthProvider, useAuth } from './contexts/AuthContext'
import { LandingPage } from '@/components/Landing'
import {
  DesktopEntryRoute,
  LoginPage,
  ProtectedRoute,
  SignupPage,
  VerifyEmailPage,
  WorkspaceEntryRoute,
} from '@/components/Auth'
import { PublicLayout } from '@/components/Layout/PublicLayout'
import { ForgotPasswordPage } from '@/components/Auth/ForgotPasswordPage'
import { ResetPasswordPage } from '@/components/Auth/ResetPasswordPage'
import { DesktopAuthorizationPage } from '@/components/Auth/DesktopAuthorizationPage'

// Modern loading fallback
const LoadingFallback = ({ label }: { label: string }) => (
  <div className="flex items-center justify-center h-64">
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      className="flex flex-col items-center gap-3"
    >
      <motion.div
        animate={{ rotate: 360 }}
        transition={{ duration: 1, repeat: Infinity, ease: 'linear' }}
      >
        <Loader2 className="w-8 h-8 text-[var(--accent)]" />
      </motion.div>
      <span className="text-sm text-[var(--text-secondary)]">{label}</span>
    </motion.div>
  </div>
)

// Super admin route guard — only SUPER_ADMIN_EMAILS can access /app/ops
const SuperAdminRoute = ({ children }: { children: React.ReactNode }) => {
  const { user } = useAuth()
  if (!user?.isSuperAdmin) {
    return <Navigate to="/app" replace />
  }
  return <>{children}</>
}

// Lazy load feature routes per Theorem 1 optimal partitioning
const ProspectsPage = lazy(() => import('@/components/Prospects/ProspectsPage').then(m => ({ default: m.ProspectsPage })))
const InboxPage = lazy(() => import('@/components/Inbox/InboxPage'))
const CampaignsPage = lazy(() => import('@/components/Campaigns/CampaignsPage').then(m => ({ default: m.CampaignsPage })))
const CampaignDetailPage = lazy(() => import('@/components/Campaigns/CampaignDetailPage').then(m => ({ default: m.CampaignDetailPage })))
const SequencesPage = lazy(() => import('@/components/Sequences/SequencesPage').then(m => ({ default: m.SequencesPage })))
const AnalyticsPage = lazy(() => import('@/components/Analytics/AnalyticsPage').then(m => ({ default: m.AnalyticsPage })))
const SettingsPage = lazy(() => import('@/components/Settings/SettingsPage').then(m => ({ default: m.default })))
const PlaybooksPage = lazy(() => import('@/components/Playbooks/PlaybooksPage').then(m => ({ default: m.PlaybooksPage })))
const IntelligencePage = lazy(() => import('@/components/Intelligence/IntelligencePage').then(m => ({ default: m.IntelligencePage })))
const AttributionDashboard = lazy(() => import('@/components/Dashboard/Attribution/AttributionDashboard').then(m => ({ default: m.AttributionDashboard })))
const EconomicAdditionalityPage = lazy(() => import('@/components/Performance/EconomicAdditionalityPage').then(m => ({ default: m.EconomicAdditionalityPage })))
const ABTestDashboard = lazy(() => import('@/components/Ads/ABTestDashboard').then(m => ({ default: m.ABTestDashboard })))
const ProspectNetworkPage = lazy(() => import('@/components/Graph/ProspectNetworkPage').then(m => ({ default: m.default })))
const SmartQueue = lazy(() => import('@/components/SmartQueue').then(m => ({ default: m.SmartQueue })))
const QueryPage = lazy(() => import('@/components/Query/QueryPage').then(m => ({ default: m.QueryPage })))

// TAG Intelligence Membrane
const TAGDashboard = lazy(() => import('@/components/TAG/TAGDashboard').then(m => ({ default: m.TAGDashboard })))
const TAGAccountDetail = lazy(() => import('@/components/TAG/TAGAccountDetail').then(m => ({ default: m.TAGAccountDetail })))

// Conversation Intelligence
const CallLibraryPage = lazy(() => import('@/components/Conversation/CallLibraryPage').then(m => ({ default: m.CallLibraryPage })))
const CallDetailPage = lazy(() => import('@/components/Conversation/CallDetailPage').then(m => ({ default: m.CallDetailPage })))

// Deal Briefing Agent
const DealBriefingList = lazy(() => import('@/components/DealBriefing/DealBriefingList').then(m => ({ default: m.DealBriefingList })))
const DealBriefingDetail = lazy(() => import('@/components/DealBriefing/DealBriefingDetail').then(m => ({ default: m.DealBriefingDetail })))

// Agent Marketplace / Network
const AgentProfilePage = lazy(() => import('@/components/Network/AgentProfilePage'))
const IntentBroadcastPage = lazy(() => import('@/components/Network/IntentBroadcastPage'))
const NetworkDirectory = lazy(() => import('@/components/Network/NetworkDirectory'))
const ContractDetailPage = lazy(() => import('@/components/Network/ContractDetailPage'))

// Billing
const BillingPage = lazy(() => import('@/components/Billing/BillingPage').then(m => ({ default: m.BillingPage })))

// Alerts
const AlertHistoryPage = lazy(() => import('@/components/Alerts/AlertHistoryPage').then(m => ({ default: m.AlertHistoryPage })))
const DiscoveryFeedPage = lazy(() => import('@/components/Discovery/DiscoveryFeedPage').then(m => ({ default: m.DiscoveryFeedPage })))
const AccountsPage = lazy(() => import('@/components/Accounts/AccountsPage').then(m => ({ default: m.AccountsPage })))
const KnowledgePage = lazy(() => import('@/components/Knowledge/KnowledgePage').then(m => ({ default: m.KnowledgePage })))
const OutreachHistoryPage = lazy(() => import('@/components/Outreach/OutreachHistoryPage').then(m => ({ default: m.OutreachHistoryPage })))
const ActivityPage = lazy(() => import('@/components/Activity/ActivityPage').then(m => ({ default: m.default })))
const DealsPage = lazy(() => import('@/components/Deals/DealsPage').then(m => ({ default: m.DealsPage })))
const CalendarPage = lazy(() => import('@/components/Calendar/CalendarPage').then(m => ({ default: m.CalendarPage })))
const WebsiteVisitorsPage = lazy(() => import('@/components/WebsiteVisitors/WebsiteVisitorsPage').then(m => ({ default: m.WebsiteVisitorsPage })))
const OperationsPage = lazy(() => import('@/components/Operations/OperationsPage').then(m => ({ default: m.OperationsPage })))
const OperationDetailPage = lazy(() => import('@/components/Operations/OperationDetailPage').then(m => ({ default: m.OperationDetailPage })))

// Ads Feature Routes
const AdsDashboardPage = lazy(() => import('@/components/Ads/AdsDashboard/AdsDashboardPage').then(m => ({ default: m.default })))
const IntentFeed = lazy(() => import('@/components/Ads/IntentFeed/IntentFeed').then(m => ({ default: m.IntentFeed })))

// Outreach Drafts
const OutreachDraftsPage = lazy(() => import('@/components/Outreach/OutreachDraftsPage').then(m => ({ default: m.OutreachDraftsPage })))

// Ops Dashboard (internal: system monitoring, failed jobs, alerting — no sidebar link)
const OpsPage = lazy(() => import('@/components/Admin/AdminPage').then(m => ({ default: m.AdminPage })))
// Competitor Comparison Pages (SEO-optimized)
// ComparisonPage removed — comparison pages targeted buyers we chose out of under the new Porter trade-offs. See /tmp/ricald-repositioning-deck.md slide 6.
// PersonaPage removed — revenue-leaders and sdr-teams personas violated Porter trade-offs #1 and #2. founder-led-sales folded into the homepage.
const PricingPage = lazy(() => import('@/components/Pricing/PricingPage').then(m => ({ default: m.PricingPage })))
const DesktopDownloadPage = lazy(() => import('@/components/Desktop/DesktopDownloadPage').then(m => ({ default: m.DesktopDownloadPage })))

// Legal Pages
const DataDisclaimer = lazy(() => import('@/components/Legal/DataDisclaimer'))
const TermsOfService = lazy(() => import('@/components/Legal/TermsOfService'))
const PrivacyPolicy = lazy(() => import('@/components/Legal/PrivacyPolicy'))
const ImpressumPage = lazy(() => import('@/components/Legal/ImpressumPage'))

// Contact (About + FAQ + Contact form)
const ContactPage = lazy(() => import('@/components/Contact/ContactPage'))

// Newsletter signup (public, no auth)
const SubscribePage = lazy(() => import('@/components/Landing/SubscribePage'))

// Investor Share View (public, no auth)
const InvestorShareView = lazy(() => import('@/components/Intelligence/InvestorShareView'))

// Help & Not Found
const HelpPage = lazy(() => import('@/components/Help/HelpPage').then(m => ({ default: m.HelpPage })))
const NotFoundPage = lazy(() => import('@/components/NotFound/NotFoundPage').then(m => ({ default: m.NotFoundPage })))

const UIShowcase = () => <div className="p-4 text-sm text-gray-600">UI components showcase</div>


export default function App() {
  return (
    <AuthProvider>
      <ThemeProvider>
        <ToastProvider>
          <Routes>
            <Route element={<PublicLayout />}>
              <Route path="/" element={
                <DesktopEntryRoute>
                  <LandingPage />
                </DesktopEntryRoute>
              } />
              <Route path="/login" element={<LoginPage />} />
              <Route path="/signup" element={<SignupPage />} />
              <Route path="/verify-email" element={<VerifyEmailPage />} />
              <Route path="/forgot-password" element={<ForgotPasswordPage />} />
              <Route path="/reset-password" element={<ResetPasswordPage />} />
              {/* /compare/:competitorSlug and /for/:personaSlug routes removed — see App.tsx header comment. */}
              <Route path="/pricing" element={
                <Suspense fallback={<LoadingFallback label="Loading pricing..." />}>
                  <PricingPage />
                </Suspense>
              } />
              <Route path="/download" element={
                <Suspense fallback={<LoadingFallback label="Loading desktop release..." />}>
                  <DesktopDownloadPage />
                </Suspense>
              } />
              <Route path="/data-disclaimer" element={
                <Suspense fallback={<LoadingFallback label="Loading disclaimer..." />}>
                  <DataDisclaimer />
                </Suspense>
              } />
              <Route path="/terms" element={
                <Suspense fallback={<LoadingFallback label="Loading terms..." />}>
                  <TermsOfService />
                </Suspense>
              } />
              <Route path="/privacy" element={
                <Suspense fallback={<LoadingFallback label="Loading privacy policy..." />}>
                  <PrivacyPolicy />
                </Suspense>
              } />
              <Route path="/impressum" element={
                <Suspense fallback={<LoadingFallback label="Loading..." />}>
                  <ImpressumPage />
                </Suspense>
              } />
              <Route path="/contact" element={
                <Suspense fallback={<LoadingFallback label="Loading..." />}>
                  <ContactPage />
                </Suspense>
              } />
              <Route path="/subscribe" element={
                <Suspense fallback={<LoadingFallback label="Loading..." />}>
                  <SubscribePage />
                </Suspense>
              } />
              <Route path="/share/investor" element={
                <Suspense fallback={<LoadingFallback label="Loading investor metrics..." />}>
                  <InvestorShareView />
                </Suspense>
              } />
            </Route>
            <Route path="/desktop/authorize" element={
              <ProtectedRoute>
                <DesktopAuthorizationPage />
              </ProtectedRoute>
            } />
            <Route path="/app" element={
              <ProtectedRoute>
                <DashboardPage />
              </ProtectedRoute>
            }>
              <Route index element={<WorkspaceEntryRoute />} />
              <Route path="dashboard" element={<Dashboard />} />
              <Route path="operations" element={
                <Suspense fallback={<LoadingFallback label="Loading Operations..." />}>
                  <OperationsPage />
                </Suspense>
              } />
              <Route path="operations/:operationId" element={
                <Suspense fallback={<LoadingFallback label="Loading Operation..." />}>
                  <OperationDetailPage />
                </Suspense>
              } />
              <Route path="inbox" element={
                <Suspense fallback={<LoadingFallback label="Loading Inbox..." />}>
                  <InboxPage />
                </Suspense>
              } />
              <Route path="intelligence" element={
                <Suspense fallback={<LoadingFallback label="Loading Intelligence..." />}>
                  <IntelligencePage />
                </Suspense>
              } />
              <Route path="prospects" element={
                <Suspense fallback={<LoadingFallback label="Loading Prospects..." />}>
                  <ProspectsPage />
                </Suspense>
              } />
              <Route path="prospects/:prospectId/network" element={
                <Suspense fallback={<LoadingFallback label="Loading Network..." />}>
                  <ProspectNetworkPage />
                </Suspense>
              } />
              {clientConfig.features.enableCampaigns ? (
                <>
                  <Route path="campaigns" element={
                    <Suspense fallback={<LoadingFallback label="Loading Campaigns..." />}>
                      <CampaignsPage />
                    </Suspense>
                  } />
                  <Route path="campaigns/:id" element={
                    <Suspense fallback={<LoadingFallback label="Loading Campaign..." />}>
                      <CampaignDetailPage />
                    </Suspense>
                  } />
                </>
              ) : (
                <>
                  <Route path="campaigns" element={<Navigate to="/app/dashboard" replace />} />
                  <Route path="campaigns/:id" element={<Navigate to="/app/dashboard" replace />} />
                </>
              )}
              <Route path="sequences" element={
                <Suspense fallback={<LoadingFallback label="Loading Sequences..." />}>
                  <SequencesPage />
                </Suspense>
              } />
              {clientConfig.features.enableAnalytics ? (
                <Route path="analytics" element={
                  <Suspense fallback={<LoadingFallback label="Loading Analytics..." />}>
                    <AnalyticsPage />
                  </Suspense>
                } />
              ) : (
                <Route path="analytics" element={<Navigate to="/app/dashboard" replace />} />
              )}
              {clientConfig.features.enablePlaybooks ? (
                <Route path="playbooks" element={
                  <Suspense fallback={<LoadingFallback label="Loading Playbooks..." />}>
                    <PlaybooksPage />
                  </Suspense>
                } />
              ) : (
                <Route path="playbooks" element={<Navigate to="/app/dashboard" replace />} />
              )}
              {clientConfig.features.enableAttribution ? (
                <Route path="attribution" element={
                  <Suspense fallback={<LoadingFallback label="Loading Attribution..." />}>
                    <AttributionDashboard />
                  </Suspense>
                } />
              ) : (
                <Route path="attribution" element={<Navigate to="/app/dashboard" replace />} />
              )}
              {/* Economic Additionality feature-flagged — metrics need real data sources */}
              {clientConfig.features.enableEconomicAdditionality ? (
                <Route path="economic-additionality" element={
                  <Suspense fallback={<LoadingFallback label="Loading Economic Impact..." />}>
                    <EconomicAdditionalityPage />
                  </Suspense>
                } />
              ) : (
                <Route path="economic-additionality" element={<Navigate to="/app/dashboard" replace />} />
              )}
              <Route path="queue" element={
                <Suspense fallback={<LoadingFallback label="Loading Priority Queue..." />}>
                  <SmartQueue />
                </Suspense>
              } />
              {/* TAG feature-flagged — needs real signal sources */}
              <Route path="accounts" element={
                <Suspense fallback={<LoadingFallback label="Loading Accounts..." />}>
                  <AccountsPage />
                </Suspense>
              } />
              <Route path="accounts/:companyId" element={
                <Suspense fallback={<LoadingFallback label="Loading Account Memory..." />}>
                  <TAGAccountDetail />
                </Suspense>
              } />
              <Route path="knowledge" element={
                <Suspense fallback={<LoadingFallback label="Loading Sources..." />}>
                  <KnowledgePage />
                </Suspense>
              } />
              {clientConfig.features.enableNativeCommercialMode ? (
                <Route path="deals" element={
                  <Suspense fallback={<LoadingFallback label="Loading Pipeline..." />}>
                    <DealsPage />
                  </Suspense>
                } />
              ) : (
                <Route path="deals" element={<Navigate to="/app/dashboard" replace />} />
              )}
              <Route path="outreach" element={
                <Suspense fallback={<LoadingFallback label="Loading Outreach..." />}>
                  <OutreachHistoryPage />
                </Suspense>
              } />
              <Route path="calendar" element={
                <Suspense fallback={<LoadingFallback label="Loading Calendar..." />}>
                  <CalendarPage />
                </Suspense>
              } />
              <Route path="website-visitors" element={
                <Suspense fallback={<LoadingFallback label="Loading Website Visitors..." />}>
                  <WebsiteVisitorsPage />
                </Suspense>
              } />
              <Route path="activity" element={
                <ErrorBoundary>
                  <Suspense fallback={<LoadingFallback label="Loading Activity..." />}>
                    <ActivityPage />
                  </Suspense>
                </ErrorBoundary>
              } />
              <Route path="approvals" element={<Navigate to="/app/activity?tab=approvals" replace />} />
              {clientConfig.features.enableTAG ? (
                <>
                  <Route path="tag" element={
                    <Suspense fallback={<LoadingFallback label="Loading TAG Intelligence..." />}>
                      <TAGDashboard />
                    </Suspense>
                  } />
                  <Route path="tag/:companyId" element={
                    <Suspense fallback={<LoadingFallback label="Loading Account Detail..." />}>
                      <TAGAccountDetail />
                    </Suspense>
                  } />
                </>
              ) : (
                <>
                  <Route path="tag" element={<Navigate to="/app/dashboard" replace />} />
                  <Route path="tag/:companyId" element={<Navigate to="/app/dashboard" replace />} />
                </>
              )}
              {/* Conversation Intelligence — feature-flagged */}
              {clientConfig.features.enableConversationIntelligence ? (
                <>
                  <Route path="conversation/calls" element={
                    <Suspense fallback={<LoadingFallback label="Loading Call Library..." />}>
                      <CallLibraryPage />
                    </Suspense>
                  } />
                  <Route path="conversation/calls/:callId" element={
                    <Suspense fallback={<LoadingFallback label="Loading Call..." />}>
                      <CallDetailPage />
                    </Suspense>
                  } />
                </>
              ) : (
                <>
                  <Route path="conversation/calls" element={<Navigate to="/app/dashboard" replace />} />
                  <Route path="conversation/calls/:callId" element={<Navigate to="/app/dashboard" replace />} />
                </>
              )}

              {/* Deal Briefing Agent — sidebar gated, routes still accessible for Slack deep links */}
              {clientConfig.features.enableDealBriefingsSidebar ? (
                <>
                  <Route path="deal-briefings" element={
                    <Suspense fallback={<LoadingFallback label="Loading Deal Briefings..." />}>
                      <DealBriefingList />
                    </Suspense>
                  } />
                  <Route path="deal-briefings/:briefingId" element={
                    <Suspense fallback={<LoadingFallback label="Loading Briefing..." />}>
                      <DealBriefingDetail />
                    </Suspense>
                  } />
                </>
              ) : (
                <>
                  <Route path="deal-briefings" element={<Navigate to="/app/dashboard" replace />} />
                  <Route path="deal-briefings/:briefingId" element={<Navigate to="/app/dashboard" replace />} />
                </>
              )}

              {/* Agent Marketplace — feature-flagged */}
              {(clientConfig.features.enableNetwork || clientConfig.features.enableMarketplace) ? (
                <>
                  <Route path="network/profile" element={
                    <Suspense fallback={<LoadingFallback label="Loading Agent Profile..." />}>
                      <AgentProfilePage />
                    </Suspense>
                  } />
                  <Route path="network/broadcast" element={
                    <Suspense fallback={<LoadingFallback label="Loading Intent Broadcasting..." />}>
                      <IntentBroadcastPage />
                    </Suspense>
                  } />
                  <Route path="network/directory" element={
                    <Suspense fallback={<LoadingFallback label="Loading Marketplace Directory..." />}>
                      <NetworkDirectory />
                    </Suspense>
                  } />
                  <Route path="network/contracts/:contractId" element={
                    <Suspense fallback={<LoadingFallback label="Loading Contract..." />}>
                      <ContractDetailPage />
                    </Suspense>
                  } />
                </>
              ) : (
                <>
                  <Route path="network/profile" element={<Navigate to="/app/dashboard" replace />} />
                  <Route path="network/broadcast" element={<Navigate to="/app/dashboard" replace />} />
                  <Route path="network/directory" element={<Navigate to="/app/dashboard" replace />} />
                  <Route path="network/contracts/:contractId" element={<Navigate to="/app/dashboard" replace />} />
                </>
              )}
              <Route path="query" element={
                <Suspense fallback={<LoadingFallback label="Loading Ask Ricald..." />}>
                  <QueryPage />
                </Suspense>
              } />
              <Route path="outreach/drafts" element={
                <Suspense fallback={<LoadingFallback label="Loading Drafts..." />}>
                  <OutreachDraftsPage />
                </Suspense>
              } />
              <Route path="alerts" element={
                <Suspense fallback={<LoadingFallback label="Loading Alerts..." />}>
                  <AlertHistoryPage />
                </Suspense>
              } />
              <Route path="discovery" element={
                <Suspense fallback={<LoadingFallback label="Loading Discovery..." />}>
                  <DiscoveryFeedPage />
                </Suspense>
              } />
              <Route path="settings" element={
                <Suspense fallback={<LoadingFallback label="Loading Settings..." />}>
                  <SettingsPage />
                </Suspense>
              } />
              <Route path="billing" element={
                <Suspense fallback={<LoadingFallback label="Loading Billing..." />}>
                  <BillingPage />
                </Suspense>
              } />
              <Route path="ops" element={
                <SuperAdminRoute>
                  <Suspense fallback={<LoadingFallback label="Loading Operations..." />}>
                    <OpsPage />
                  </Suspense>
                </SuperAdminRoute>
              } />
              {/* RFC-078 makes Desktop the single installable product. Keep
                  historical Hands URLs useful without preserving a second
                  customer setup or download journey. */}
              <Route path="hands" element={<Navigate to="/download" replace />} />
              <Route path="hands/setup" element={<Navigate to="/download" replace />} />
              <Route path="help" element={
                <Suspense fallback={<LoadingFallback label="Loading Help..." />}>
                  <HelpPage />
                </Suspense>
              } />
              <Route path="ui" element={<UIShowcase />} />

              {/* Google Ads is connectable in every production workspace, so its
                  destination must exist whenever the integration card exists. */}
              <Route path="ads">
                <Route index element={
                  <Suspense fallback={<LoadingFallback label="Loading Ads Dashboard..." />}>
                    <AdsDashboardPage />
                  </Suspense>
                } />
                {clientConfig.features.enableAds ? (
                  <Route path="ab-testing" element={
                    <Suspense fallback={<LoadingFallback label="Loading A/B Testing..." />}>
                      <ABTestDashboard />
                    </Suspense>
                  } />
                ) : (
                  <Route path="ab-testing" element={<Navigate to="/app/ads" replace />} />
                )}
                <Route path="campaigns" element={<Navigate to="/app/ads" replace />} />
                <Route path="campaigns/new" element={<Navigate to="/app/ads" replace />} />
              </Route>
              {clientConfig.features.enableAds ? (
                <Route path="hot-prospects" element={
                    <Suspense fallback={<LoadingFallback label="Loading Hot Prospects..." />}>
                      <IntentFeed />
                    </Suspense>
                } />
              ) : (
                <Route path="hot-prospects" element={<Navigate to="/app/ads" replace />} />
              )}
            </Route>
            <Route path="*" element={
              <Suspense fallback={<LoadingFallback label="" />}>
                <NotFoundPage />
              </Suspense>
            } />
          </Routes>
        </ToastProvider>
      </ThemeProvider>
    </AuthProvider>
  )
}
